import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

const ADMIN_TOKEN_KEY = 'admin_token';

export interface AdminUser {
  username: string;
}

export interface AdminAuthResponse {
  token: string;
  user: AdminUser;
}

export interface Tool {
  id: number;
  name: string;
  slug: string;
  webhookUrl: string | null;
  logoUrl: string | null;
  paymentDescription: string | null;
  companyName: string | null;
  supportEmail: string | null;
  billingDay: number;
  active: boolean;
  testMode: boolean;
  createdAt: string;
  stripeConfigured: boolean;
  mollieConfigured: boolean;
  webhookSecretConfigured: boolean;
  stripeApiKeyPreview: string | null;
  stripeTestApiKeyPreview: string | null;
  stripeWebhookSecretPreview: string | null;
  mollieApiKeyPreview: string | null;
  mollieTestApiKeyPreview: string | null;
}

export interface ToolUpdate {
  name?: string;
  webhookUrl?: string | null;
  webhookSecret?: string | null;
  logoUrl?: string | null;
  paymentDescription?: string | null;
  companyName?: string | null;
  supportEmail?: string | null;
  billingDay?: number;
  active?: boolean;
  testMode?: boolean;
}

export interface ToolProviderConfigUpdate {
  stripeApiKey?: string;
  stripeTestApiKey?: string;
  stripeWebhookSecret?: string;
  mollieApiKey?: string;
  mollieTestApiKey?: string;
}

export interface CredentialResult {
  id: number;
  key: string;
  keyPrefix: string;
  createdAt: string;
}

export interface Payment {
  id: number;
  toolId: number | null;
  toolName: string | null;
  provider: string;
  providerPaymentId: string;
  status: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface Subscription {
  id: number;
  toolId: number | null;
  toolName: string | null;
  toolUserReference: string;
  provider: string;
  status: string;
  amountCents: number;
  currency: string;
  interval: string;
  nextBillingDate: string | null;
  failedChargeCount: number;
  createdAt: string | null;
}

export interface IncomingWebhook {
  id: number;
  provider: string;
  endpoint: string;
  payload: string | null;
  status: string;
  processingError: string | null;
  tool: { id: number; name: string } | null;
  createdAt: string;
  processedAt: string | null;
  replayCount: number;
  lastReplayedAt: string | null;
}

export interface IncomingWebhookPage {
  items: IncomingWebhook[];
  total: number;
  page: number;
  limit: number;
}

export interface AuditLogEntry {
  id: number;
  direction: string;
  method: string;
  path: string;
  statusCode: number | null;
  requestBody: string | null;
  responseBody: string | null;
  createdAt: string;
}

export interface Cron {
  id: number;
  name: string;
  command: string;
  commandArguments: Record<string, unknown> | null;
  schedule: string;
  enabled: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  timezone: string;
}

export interface CronCreateUpdate {
  name: string;
  command: string;
  commandArguments?: Record<string, unknown> | null;
  schedule: string;
  enabled: boolean;
  timezone: string;
}

export type VatMode = 'inclusive' | 'exclusive';

export interface EmailTemplatePlaceholder {
  name: string;
  description: string;
}

export interface EmailTemplateSummary {
  key: string;
  label: string;
  enabled: boolean;
  isOverridden: boolean;
  subject: string;
}

export interface EmailTemplateList {
  toolId: number;
  placeholders: EmailTemplatePlaceholder[];
  templates: EmailTemplateSummary[];
}

export interface EmailTemplateDetail {
  key: string;
  label: string;
  enabled: boolean;
  isOverridden: boolean;
  subject: string;
  bodyHtml: string;
  default: { subject: string; bodyHtml: string };
  updatedAt: string | null;
}

export interface EmailTemplateUpdate {
  subject?: string | null;
  bodyHtml?: string | null;
  enabled?: boolean;
}

export interface EmailTemplatePreview {
  subject: string;
  html: string;
}

export interface InvoiceTemplate {
  id: number | null;
  ownerKey: string;
  enabled: boolean;
  logoPath: boolean;
  logoUrl: string | null;
  companyName: string | null;
  street: string | null;
  houseNumber: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  vatNumber: string | null;
  cocNumber: string | null;
  iban: string | null;
  email: string | null;
  footerText: string | null;
  accentColor: string | null;
  numberPrefix: string | null;
  vatRate: number;
  vatMode: VatMode;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface InvoiceTemplateUpdate {
  enabled?: boolean;
  companyName?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  vatNumber?: string | null;
  cocNumber?: string | null;
  iban?: string | null;
  email?: string | null;
  footerText?: string | null;
  accentColor?: string | null;
  numberPrefix?: string | null;
  vatRate?: number;
  vatMode?: VatMode;
}

export interface InvoiceSummary {
  id: number;
  number: string;
  ownerKey: string;
  sourceType: string | null;
  sourceId: string | null;
  customerName: string | null;
  customerEmail: string | null;
  currency: string;
  netCents: number;
  vatCents: number;
  grossCents: number;
  status: string;
  hasPdf: boolean;
  issuedAt: string | null;
  createdAt: string | null;
}

export interface InvoiceParty {
  name?: string | null;
  companyName?: string | null;
  email?: string | null;
  vatNumber?: string | null;
  cocNumber?: string | null;
  iban?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  unitPriceCents: number;
  netCents: number;
  vatCents: number;
  grossCents: number;
  vatRate: number;
}

export interface InvoiceRelatedPayment {
  id: number | null;
  provider: string | null;
  providerPaymentId: string | null;
  status: string | null;
  amountCents: number;
  currency: string;
  description: string | null;
  sequenceType: string | null;
  failureReason: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface InvoiceRelatedTool {
  id: number | null;
  name: string | null;
  companyName: string | null;
  supportEmail: string | null;
}

export interface InvoiceRelatedSubscription {
  id: number | null;
  provider: string | null;
  providerSubscriptionId: string | null;
  status: string | null;
  amountCents: number;
  currency: string;
  interval: string;
  description: string | null;
  nextBillingDate: string | null;
  endsAt: string | null;
}

export interface InvoiceRelated {
  payment: InvoiceRelatedPayment;
  tool: InvoiceRelatedTool | null;
  subscription: InvoiceRelatedSubscription | null;
}

export interface InvoiceDetail {
  id: number;
  number: string;
  ownerKey: string;
  sourceType: string | null;
  sourceId: string | null;
  issuer: InvoiceParty;
  customer: InvoiceParty;
  currency: string;
  netCents: number;
  vatCents: number;
  grossCents: number;
  vatRate: number;
  vatMode: string;
  status: string;
  hasPdf: boolean;
  issuedAt: string | null;
  createdAt: string | null;
  items: InvoiceItem[];
  related: InvoiceRelated | null;
}

@Injectable({ providedIn: 'root' })
export class AdminService {
  private tokenSignal = signal<string | null>(null);
  private adminUserSignal = signal<AdminUser | null>(null);

  isAdminLoggedIn = computed(() => this.tokenSignal() !== null);
  adminUser = this.adminUserSignal.asReadonly();

  private get baseUrl(): string {
    return `${environment.apiUrl}/admin`;
  }

  private get headers(): { [key: string]: string } {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  constructor(private http: HttpClient) {
    const t = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (t) {
      this.tokenSignal.set(t);
      try {
        const payload = JSON.parse(atob(t.split('.')[1]));
        if (payload.exp && payload.exp > Date.now() / 1000) {
          this.adminUserSignal.set({ username: payload.username ?? '' });
        } else {
          this.logout();
        }
      } catch {
        this.logout();
      }
    }
  }

  getToken(): string | null {
    return this.tokenSignal() ?? localStorage.getItem(ADMIN_TOKEN_KEY);
  }

  login(username: string, password: string): Observable<AdminAuthResponse> {
    return this.http
      .post<AdminAuthResponse>(`${this.baseUrl}/login`, { username, password })
      .pipe(
        tap((res) => {
          localStorage.setItem(ADMIN_TOKEN_KEY, res.token);
          this.tokenSignal.set(res.token);
          this.adminUserSignal.set(res.user);
        })
      );
  }

  logout(): void {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    this.tokenSignal.set(null);
    this.adminUserSignal.set(null);
  }

  getTools(): Observable<Tool[]> {
    return this.http.get<Tool[]>(`${this.baseUrl}/tools`, {
      headers: this.headers,
    });
  }

  createTool(name: string, webhookUrl?: string): Observable<Tool> {
    return this.http.post<Tool>(
      `${this.baseUrl}/tools`,
      { name, webhookUrl: webhookUrl || null },
      { headers: this.headers }
    );
  }

  getTool(id: number): Observable<Tool> {
    return this.http.get<Tool>(`${this.baseUrl}/tools/${id}`, {
      headers: this.headers,
    });
  }

  updateTool(id: number, data: ToolUpdate): Observable<Tool> {
    return this.http.patch<Tool>(`${this.baseUrl}/tools/${id}`, data, {
      headers: this.headers,
    });
  }

  updateToolProviderConfig(id: number, data: ToolProviderConfigUpdate): Observable<Tool> {
    return this.http.put<Tool>(`${this.baseUrl}/tools/${id}/provider-config`, data, {
      headers: this.headers,
    });
  }

  uploadToolLogo(id: number, file: File): Observable<Tool> {
    const formData = new FormData();
    formData.append('logo', file);
    return this.http.post<Tool>(`${this.baseUrl}/tools/${id}/logo`, formData, {
      headers: { ...this.headers, Accept: 'application/json' },
    });
  }

  getToolLogoUrl(toolId: number): string {
    return `${environment.apiUrl}/admin/tools/${toolId}/logo`;
  }

  getToolLogoBlob(toolId: number): Observable<Blob> {
    return this.http.get(this.getToolLogoUrl(toolId), {
      headers: this.headers,
      responseType: 'blob',
    });
  }

  deleteTool(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/tools/${id}`, {
      headers: this.headers,
    });
  }

  generateCredential(toolId: number): Observable<CredentialResult> {
    return this.http.post<CredentialResult>(
      `${this.baseUrl}/tools/${toolId}/credentials`,
      {},
      { headers: this.headers }
    );
  }

  getPayments(): Observable<Payment[]> {
    return this.http.get<Payment[]>(`${this.baseUrl}/payments`, {
      headers: this.headers,
    });
  }

  getPayment(id: number): Observable<Payment> {
    return this.http.get<Payment>(`${this.baseUrl}/payments/${id}`, {
      headers: this.headers,
    });
  }

  getCrons(): Observable<Cron[]> {
    return this.http.get<Cron[]>(`${this.baseUrl}/crons`, {
      headers: this.headers,
    });
  }

  getCron(id: number): Observable<Cron> {
    return this.http.get<Cron>(`${this.baseUrl}/crons/${id}`, {
      headers: this.headers,
    });
  }

  createCron(data: CronCreateUpdate): Observable<Cron> {
    return this.http.post<Cron>(`${this.baseUrl}/crons`, data, {
      headers: this.headers,
    });
  }

  updateCron(id: number, data: CronCreateUpdate): Observable<Cron> {
    return this.http.put<Cron>(`${this.baseUrl}/crons/${id}`, data, {
      headers: this.headers,
    });
  }

  deleteCron(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/crons/${id}`, {
      headers: this.headers,
    });
  }

  getSubscriptions(): Observable<Subscription[]> {
    return this.http.get<Subscription[]>(`${this.baseUrl}/subscriptions`, {
      headers: this.headers,
    });
  }

  getToolAuditLogs(toolId: number): Observable<AuditLogEntry[]> {
    return this.http.get<AuditLogEntry[]>(`${this.baseUrl}/tools/${toolId}/audit-logs`, {
      headers: this.headers,
    });
  }

  getToolIncomingWebhooks(toolId: number, page = 1, limit = 25): Observable<IncomingWebhookPage> {
    return this.http.get<IncomingWebhookPage>(
      `${this.baseUrl}/incoming-webhooks?toolId=${toolId}&page=${page}&limit=${limit}`,
      { headers: this.headers }
    );
  }

  replayIncomingWebhook(webhookId: number): Observable<{ success: boolean; statusCode: number }> {
    return this.http.post<{ success: boolean; statusCode: number }>(
      `${this.baseUrl}/incoming-webhooks/${webhookId}/replay`,
      {},
      { headers: this.headers }
    );
  }

  getInvoiceTemplate(toolId: number): Observable<InvoiceTemplate> {
    return this.http.get<InvoiceTemplate>(`${this.baseUrl}/tools/${toolId}/invoice-template`, {
      headers: this.headers,
    });
  }

  updateInvoiceTemplate(toolId: number, data: InvoiceTemplateUpdate): Observable<InvoiceTemplate> {
    return this.http.put<InvoiceTemplate>(`${this.baseUrl}/tools/${toolId}/invoice-template`, data, {
      headers: this.headers,
    });
  }

  getEmailTemplates(toolId: number): Observable<EmailTemplateList> {
    return this.http.get<EmailTemplateList>(`${this.baseUrl}/tools/${toolId}/email-templates`, {
      headers: this.headers,
    });
  }

  getEmailTemplate(toolId: number, key: string): Observable<EmailTemplateDetail> {
    return this.http.get<EmailTemplateDetail>(`${this.baseUrl}/tools/${toolId}/email-templates/${key}`, {
      headers: this.headers,
    });
  }

  updateEmailTemplate(toolId: number, key: string, data: EmailTemplateUpdate): Observable<EmailTemplateDetail> {
    return this.http.put<EmailTemplateDetail>(`${this.baseUrl}/tools/${toolId}/email-templates/${key}`, data, {
      headers: this.headers,
    });
  }

  resetEmailTemplate(toolId: number, key: string): Observable<EmailTemplateDetail> {
    return this.http.delete<EmailTemplateDetail>(`${this.baseUrl}/tools/${toolId}/email-templates/${key}`, {
      headers: this.headers,
    });
  }

  previewEmailTemplate(toolId: number, key: string): Observable<EmailTemplatePreview> {
    return this.http.get<EmailTemplatePreview>(`${this.baseUrl}/tools/${toolId}/email-templates/${key}/preview`, {
      headers: this.headers,
    });
  }

  uploadInvoiceLogo(toolId: number, file: File): Observable<InvoiceTemplate> {
    const formData = new FormData();
    formData.append('logo', file);
    return this.http.post<InvoiceTemplate>(
      `${this.baseUrl}/tools/${toolId}/invoice-template/logo`,
      formData,
      { headers: { ...this.headers, Accept: 'application/json' } }
    );
  }

  getInvoiceTemplateLogoUrl(toolId: number): string {
    return `${environment.apiUrl}/admin/tools/${toolId}/invoice-template/logo`;
  }

  getInvoiceTemplateLogoBlob(toolId: number): Observable<Blob> {
    return this.http.get(this.getInvoiceTemplateLogoUrl(toolId), {
      headers: this.headers,
      responseType: 'blob',
    });
  }

  getInvoices(toolId?: number): Observable<InvoiceSummary[]> {
    const query = toolId ? `?toolId=${toolId}` : '';
    return this.http.get<InvoiceSummary[]>(`${this.baseUrl}/invoices${query}`, {
      headers: this.headers,
    });
  }

  getInvoice(invoiceId: number): Observable<InvoiceDetail> {
    return this.http.get<InvoiceDetail>(`${this.baseUrl}/invoices/${invoiceId}`, {
      headers: this.headers,
    });
  }

  getInvoicePdfUrl(invoiceId: number): string {
    return `${environment.apiUrl}/admin/invoices/${invoiceId}/pdf`;
  }

  getInvoicePdfBlob(invoiceId: number): Observable<Blob> {
    return this.http.get(this.getInvoicePdfUrl(invoiceId), {
      headers: this.headers,
      responseType: 'blob',
    });
  }

  resendInvoice(invoiceId: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.baseUrl}/invoices/${invoiceId}/resend`,
      {},
      { headers: this.headers }
    );
  }

  generateInvoiceForPayment(paymentId: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.baseUrl}/payments/${paymentId}/invoice`,
      {},
      { headers: this.headers }
    );
  }
}
